import { useCallback, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Code,
  Grid,
  GridItem,
  Link,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { ListCard } from '@duke-hq/ui/components/Cards/ListCard';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import type { EmailTemplateCopy, TemplateVariable } from '@duke-hq/email-templates';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { withPermission } from '../withPermission';
import { LockedBadge } from './LockedBadge';
import { TemplateEditor } from './TemplateEditor';
import { TemplatePreview } from './TemplatePreview';
import { testIds } from './testIds';

/**
 * One email: what it says, what it can say, and what it looks like.
 *
 * While it is being edited the preview follows the draft, not the stored row.
 */

function CopyField({
  label,
  value,
}: {
  label: React.ReactNode;
  value: string;
}): React.ReactElement {
  return (
    <Stack gap={1}>
      <Text fontSize="sm" fontWeight="medium">
        {label}
      </Text>
      <Text fontSize="sm" color="fg.muted">
        {value}
      </Text>
    </Stack>
  );
}

function VariablesTable({ variables }: { variables: TemplateVariable[] }): React.ReactElement {
  if (variables.length === 0) {
    return (
      <Text fontSize="sm" color="fg.muted">
        <Trans>This email takes no values. Its wording is the same every time.</Trans>
      </Text>
    );
  }

  return (
    <Table size="sm" data-testid={testIds.variablesTable}>
      <Thead>
        <Tr>
          <Th>
            <Trans>Value</Trans>
          </Th>
          <Th>
            <Trans>Availability</Trans>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {variables.map((variable) => (
          <Tr key={variable.path}>
            <Td>
              <Stack gap={1}>
                {variable.description == null ? null : (
                  <Text fontSize="sm">{variable.description}</Text>
                )}
                <Code fontSize="xs" width="fit-content">
                  {variable.path}
                </Code>
              </Stack>
            </Td>
            <Td>
              {variable.optional ? (
                // "Not always" read as a rare exception. It is often the norm —
                // most consultant referrals carry no purchase order, and a fifth
                // of courses have no code — and the sentence around it is what
                // an author has to make work when it is missing.
                <Text fontSize="sm" color="fg.muted">
                  <Trans>Sometimes missing</Trans>
                </Text>
              ) : (
                <Text fontSize="sm">
                  <Trans>Always</Trans>
                </Text>
              )}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function TemplateDetailPageContent(): React.ReactElement {
  const { templateName = '' } = useParams();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<EmailTemplateCopy>();
  const utils = trpcAdmin.useUtils();
  const template = trpcAdmin.emailTemplates.get.useQuery({ templateName });
  const save = trpcAdmin.emailTemplates.save.useMutation({
    onSuccess: async () => {
      setEditing(false);
      setDraft(undefined);
      // Every draft key, not just the stored one: each draft was its own query.
      await Promise.all([
        utils.emailTemplates.get.invalidate({ templateName }),
        utils.emailTemplates.preview.invalidate(),
      ]);
    },
  });

  const stopEditing = useCallback(() => {
    setEditing(false);
    setDraft(undefined);
  }, []);

  if (template.isLoading) {
    return (
      <Box p={4}>
        <Spinner />
      </Box>
    );
  }

  if (template.data == null) {
    return (
      <Box p={4}>
        <Text>
          <Trans>This email could not be found.</Trans>
        </Text>
      </Box>
    );
  }

  const {
    condition,
    label,
    copy,
    declared,
    variables,
    locked,
    description,
    storedCopyInUse,
    storedCopyUnreadable,
    fragments,
    action,
  } = template.data;

  // The organisation's row, or Duke's declaration for a template it cannot
  // reword. Both hold the same two fields, so one render path serves them and
  // only the sentence below says who wrote them.
  const wording = copy ?? declared;

  return (
    <Stack gap={4} p={4}>
      <Link as={RouterLink} to="/templates" fontSize="sm" display="inline-flex" alignItems="center">
        <ArrowLeft size={iconSize.sm} />
        <Box as="span" ml={1}>
          <Trans>All templates</Trans>
        </Box>
      </Link>

      <Stack gap={1}>
        <PageHeading data-testid={testIds.heading}>{label ?? templateName}</PageHeading>
        {condition == null ? null : (
          <Text color="fg.muted" fontSize="sm">
            {condition}
          </Text>
        )}
        <Text fontSize="sm">{description}</Text>
        {locked === 'codeOwned' ? (
          <Box>
            <LockedBadge />
          </Box>
        ) : null}
      </Stack>

      {/* Suppressed for an unreadable row: this promises that saved wording takes
          effect once the switch is on, and that row's own warning says the
          opposite -- it will fail the send. That warning covers both switch
          states itself, so it is the one to keep. */}
      {storedCopyInUse || storedCopyUnreadable ? null : (
        <Alert status="info" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription fontSize="sm" data-testid={testIds.storedCopyNotInUse}>
            {/* The preview still shows the wording being authored, because that is
                what it is for. What it cannot show is that nobody is receiving it
                yet. Worded to read correctly before anything has been saved too,
                since knowing this before writing is the point. */}
            <Trans>
              Emails still go out with the default wording. Anything saved here takes effect once
              stored wording is turned on.
            </Trans>
          </AlertDescription>
        </Alert>
      )}

      <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={4}>
        <GridItem>
          <Stack gap={4}>
            <ListCard
              cardTitle={<Trans>Wording</Trans>}
              cardSubtitle={<Trans>The subject and preview line this email sends.</Trans>}
              cardContent={
                storedCopyUnreadable ? (
                  <Alert
                    status={storedCopyInUse ? 'error' : 'warning'}
                    borderRadius="md"
                    alignItems="flex-start"
                  >
                    <AlertIcon />
                    <AlertDescription fontSize="sm" data-testid={testIds.storedCopyUnreadable}>
                      {/* What happens next differs entirely by the switch: with it
                        off the sender never reads this row, and with it on the send
                        fails rather than falling back. Saying only that the wording
                        is unreadable would leave the reader unable to tell an
                        eventual problem from a live one. */}
                      {storedCopyInUse ? (
                        <Trans>
                          The saved wording for this email cannot be read, so this email is failing
                          to send. Nobody is receiving it until it is fixed.
                        </Trans>
                      ) : (
                        <Trans>
                          The saved wording for this email cannot be read. Nothing is affected while
                          emails still go out with the default wording, but this email will fail to
                          send once saved wording is turned on.
                        </Trans>
                      )}
                    </AlertDescription>
                  </Alert>
                ) : editing ? (
                  <TemplateEditor
                    copy={
                      copy ?? {
                        subject: '',
                        previewText: '',
                        body: { type: 'doc', content: [] },
                      }
                    }
                    variables={variables}
                    fragment={fragments[0]}
                    action={action}
                    saving={save.isPending}
                    problem={save.error?.message}
                    onSave={(edited) => save.mutate({ templateName, ...edited })}
                    onDraftChange={setDraft}
                    onCancel={stopEditing}
                  />
                ) : copy == null && locked !== 'codeOwned' ? (
                  // Editing would open on an empty form, so the whole email would
                  // have to be retyped from what the preview shows. Every editable
                  // template is given its wording when an organisation is set up,
                  // so this says what is missing rather than offering the trap.
                  <Alert status="info" borderRadius="md" alignItems="flex-start">
                    <AlertIcon />
                    <AlertDescription fontSize="sm" data-testid={testIds.wordingNotSetUp}>
                      <Trans>
                        No wording has been saved for your organisation yet, so there is nothing
                        here to edit. The email still sends, using the default wording shown in the
                        preview.
                      </Trans>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Stack gap={3}>
                    {wording != null && (
                      <Stack gap={3}>
                        <CopyField label={<Trans>Subject</Trans>} value={wording.subject} />
                        <CopyField
                          label={<Trans>Preview text</Trans>}
                          value={wording.previewText}
                        />
                      </Stack>
                    )}
                    {copy == null && (
                      <Text fontSize="sm" color="fg.muted" data-testid={testIds.defaultWording}>
                        <Trans>
                          This email uses the wording Duke sets, which is the same for every
                          organisation.
                        </Trans>
                      </Text>
                    )}
                    {locked === 'codeOwned' ? null : (
                      <Box>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditing(true)}
                          data-testid={testIds.editButton}
                        >
                          <Trans>Edit wording</Trans>
                        </Button>
                      </Box>
                    )}
                  </Stack>
                )
              }
            />

            <ListCard
              cardTitle={<Trans>Values this email can use</Trans>}
              cardSubtitle={
                <Trans>
                  Filled in when the email is sent. Write the sentence around a value that is
                  sometimes missing so it still reads when there is nothing to put there.
                </Trans>
              }
              cardContent={<VariablesTable variables={variables} />}
            />
          </Stack>
        </GridItem>

        <GridItem>
          <ListCard
            cardTitle={<Trans>Preview</Trans>}
            cardSubtitle={
              <Trans>
                The whole email, with your branding. Values are samples, not real student details.
              </Trans>
            }
            cardContent={<TemplatePreview templateName={templateName} draft={draft} />}
          />
        </GridItem>
      </Grid>
    </Stack>
  );
}

export const TemplateDetailPage = withPermission('canManageTemplates', TemplateDetailPageContent);
